// The ONE adapter NavSection[] -> NavigationConfigSchema['routes'].
//
// Creates the JSX Icon, maps defaultOpen -> collapsed: !defaultOpen, and drops
// the favorites/adminTools sentinels (they are NOT schema routes — the
// section-dispatch render surfaces consume the raw NavSection[] for those).
// The schema's Icon field is z.custom<React.ReactNode>(), so JSX is accepted
// without any unsafe type cast (Critical Rule #2).

import { z } from 'zod';

import { NavigationConfigSchema } from '@kit/ui/navigation-schema';
import { Trans } from '@kit/ui/trans';

import type { NavSection } from './nav-types';

const iconClasses = 'w-4';

/**
 * The "New" launch pill rendered after a nav label when `NavItemDef.isNew`
 * is set. Shared by the desktop adapter below and the mobile nav renderers.
 */
export function NavNewBadge() {
  return (
    <span className="bg-primary/10 text-primary ml-auto rounded-full px-1.5 py-0.5 text-[10px] leading-none font-semibold">
      <Trans i18nKey="common:newBadge" />
    </span>
  );
}

type ConfigRoutes = z.infer<typeof NavigationConfigSchema>['routes'];

/**
 * Convert resolved sections into the navigation-schema route array consumed by
 * the badges renderer. Sentinel sections (favorites, adminTools) are not schema
 * routes and are skipped here. Dividers map to the schema's `{ divider: true }`.
 */
export function sectionsToConfigRoutes(sections: NavSection[]): ConfigRoutes {
  const routes: ConfigRoutes = [];

  for (const section of sections) {
    if (section.kind === 'divider') {
      routes.push({ divider: true });
      continue;
    }

    // favorites / adminTools are render-layer sentinels, not schema routes.
    if (section.kind !== 'group') continue;

    routes.push({
      label: section.i18nKey,
      collapsible: section.collapsible ?? false,
      collapsed: !(section.defaultOpen ?? true),
      children: section.items.map((item) => ({
        label: item.i18nKey,
        path: item.path,
        Icon: <item.icon className={iconClasses} />,
        ...(item.end !== undefined ? { end: item.end } : {}),
        ...(item.isNew ? { renderAction: <NavNewBadge /> } : {}),
      })),
    });
  }

  return routes;
}
